import React, { useState } from "react";
import {
  Search,
  User,
  Briefcase,
  Calendar,
  X,
  RefreshCw,
  ChevronRight,
  TrendingUp,
} from "lucide-react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "ILS",
    maximumFractionDigits: 0,
  });

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@500&display=swap');

  .bse * { box-sizing: border-box; margin: 0; padding: 0; }

  .bse {
    font-family: 'Plus Jakarta Sans', sans-serif;
    background: #F0F2F7;
    min-height: 100vh;
    color: #111827;
  }

  /* Header */
  .bse-header {
    background: #fff;
    border-bottom: 1px solid #E5E7EB;
    padding: 18px 20px 14px;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  .bse-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }
  .bse-eyebrow {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: #3B82F6;
    margin-bottom: 2px;
  }
  .bse-title {
    font-size: 20px;
    font-weight: 700;
    color: #111827;
    letter-spacing: -0.02em;
  }

  .bse-refresh {
    width: 34px; height: 34px;
    border-radius: 8px;
    border: 1px solid #E5E7EB;
    background: #fff;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    color: #6B7280;
    transition: all 0.15s;
    flex-shrink: 0;
  }
  .bse-refresh:hover { border-color: #3B82F6; color: #3B82F6; background: #EFF6FF; }
  .bse-refresh:disabled { opacity: 0.4; cursor: not-allowed; }

  .bse-pills { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 12px; }
  .bse-pill {
    display: flex; align-items: center; gap: 5px;
    background: #F9FAFB;
    border: 1px solid #E5E7EB;
    border-radius: 20px;
    padding: 4px 10px;
    font-size: 11px;
    color: #6B7280;
    font-weight: 500;
  }
  .bse-pill-dot { width: 6px; height: 6px; border-radius: 50%; }
  .bse-pill b { color: #111827; font-weight: 600; }

  .bse-search { position: relative; }
  .bse-search svg { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #9CA3AF; pointer-events: none; }
  .bse-search input {
    width: 100%;
    padding: 9px 34px 9px 32px;
    background: #F9FAFB;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    color: #111827;
    outline: none;
    transition: all 0.15s;
  }
  .bse-search input::placeholder { color: #9CA3AF; }
  .bse-search input:focus { border-color: #3B82F6; background: #fff; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
  .bse-clear {
    position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
    width: 18px; height: 18px; border-radius: 4px;
    background: #E5E7EB; border: none;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; color: #6B7280; transition: all 0.15s;
  }
  .bse-clear:hover { background: #3B82F6; color: #fff; }

  .bse-body {
    padding: 14px 16px 32px;
    overflow-y: auto;
    max-height: calc(100vh - 200px);
  }
  .bse-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
    gap: 10px;
  }

  /* Card */
  .bse-card {
    background: #fff;
    border: 1px solid #E5E7EB;
    border-radius: 12px;
    padding: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
    animation: bse-up 0.3s ease both;
    position: relative;
    overflow: hidden;
  }
  .bse-card:hover {
    border-color: #3B82F6;
    box-shadow: 0 4px 16px rgba(59,130,246,0.12);
    transform: translateY(-2px);
  }
  .bse-card:hover .bse-card-accent { opacity: 1; }
  .bse-card-accent {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, #3B82F6, #6366F1);
    opacity: 0;
    transition: opacity 0.2s;
  }

  .bse-card-top {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }
  .bse-avatar {
    width: 38px; height: 38px;
    border-radius: 10px;
    background: linear-gradient(135deg, #DBEAFE, #BFDBFE);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    transition: transform 0.2s;
  }
  .bse-card:hover .bse-avatar { transform: scale(1.06); }
  .bse-avatar.mgr { background: linear-gradient(135deg, #FEF3C7, #FDE68A); }
  .bse-card-info { min-width: 0; flex: 1; }
  .bse-card-name {
    font-size: 13px; font-weight: 600;
    color: #111827;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    line-height: 1.3;
  }
  .bse-card-id {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px; color: #9CA3AF;
    letter-spacing: 0.06em;
    margin-top: 1px;
  }

  .bse-badge {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 8px;
    border-radius: 6px;
    font-size: 10px; font-weight: 600;
    letter-spacing: 0.03em;
    margin-bottom: 10px;
  }
  .bse-badge.emp { background: #EFF6FF; color: #3B82F6; }
  .bse-badge.mgr { background: #FFFBEB; color: #D97706; }

  .bse-salary {
    display: flex; align-items: center;
    justify-content: space-between;
    background: #F9FAFB;
    border-radius: 8px;
    padding: 8px 10px;
    margin-bottom: 10px;
  }
  .bse-salary-lbl { font-size: 9px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: #9CA3AF; margin-bottom: 2px; }
  .bse-salary-amt { font-size: 14px; font-weight: 700; letter-spacing: -0.01em; }
  .bse-salary-icon {
    width: 28px; height: 28px; border-radius: 7px;
    display: flex; align-items: center; justify-content: center;
  }

  .bse-cta {
    display: flex; align-items: center; justify-content: space-between;
    padding-top: 8px;
    border-top: 1px solid #F3F4F6;
  }
  .bse-cta span { font-size: 11px; font-weight: 600; color: #3B82F6; }
  .bse-cta-arr {
    width: 20px; height: 20px; border-radius: 5px;
    background: #EFF6FF;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.15s;
  }
  .bse-card:hover .bse-cta-arr { background: #3B82F6; }
  .bse-card:hover .bse-cta-arr svg { color: #fff !important; }

  .bse-empty {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 60px 20px; text-align: center;
  }
  .bse-empty-ico {
    width: 56px; height: 56px; border-radius: 14px;
    background: #fff; border: 1px solid #E5E7EB;
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 14px;
  }
  .bse-empty-t { font-size: 15px; font-weight: 600; color: #111827; margin-bottom: 6px; }
  .bse-empty-s { font-size: 12px; color: #6B7280; line-height: 1.6; max-width: 240px; }

  .bse-overlay {
    position: fixed; inset: 0;
    background: rgba(255,255,255,0.8);
    backdrop-filter: blur(4px);
    display: flex; align-items: center; justify-content: center;
    z-index: 50;
  }
  .bse-spinner {
    width: 36px; height: 36px;
    border: 3px solid #E5E7EB;
    border-top-color: #3B82F6;
    border-radius: 50%;
    animation: bse-spin 0.75s linear infinite;
  }
  .bse-spin-lbl { font-size: 12px; color: #6B7280; margin-top: 10px; font-weight: 500; }

  @keyframes bse-spin { to { transform: rotate(360deg); } }
  @keyframes bse-up {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

export default function BossShowEmployee({ onRefresh, refreshing, allData }) {
  const { t } = useTranslation();
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState("");

  const getSalaryInfo = (item) =>
    item.typeSalary === 2
      ? {
          amount: item.monthlySalary || 0,
          label: t("monthly"),
          color: "#7C3AED",
          bg: "#EDE9FE",
        }
      : {
          amount: item.salary || 0,
          label: t("daily"),
          color: "#059669",
          bg: "#D1FAE5",
        };

  const allActive = allData?.myEmployees?.filter((e) => e.active === 1) || [];
  const managers = allActive.filter((e) => e.type === 2).length;

  const filtered = searchQuery.trim()
    ? allActive.filter((e) =>
        e.name.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : allActive;

  const handleClick = (employee) => {
    localStorage.setItem("choosedEmployee", JSON.stringify(employee));
    localStorage.setItem("allData", JSON.stringify(allData));
    history.push(`/boss-employee-statistics/${employee.id}`);
  };

  return (
    <div className="bse">
      <style>{STYLES}</style>

      <header className="bse-header">
        <div className="bse-header-row">
          <div>
            <p className="bse-eyebrow">{t("workforce")}</p>
            <h1 className="bse-title">{t("myEmployees")}</h1>
          </div>
          <button
            className="bse-refresh"
            onClick={onRefresh}
            disabled={refreshing}
            title="Refresh"
          >
            <RefreshCw
              size={15}
              style={
                refreshing
                  ? { animation: "bse-spin 0.75s linear infinite" }
                  : {}
              }
            />
          </button>
        </div>

        <div className="bse-pills">
          <div className="bse-pill">
            <span className="bse-pill-dot" style={{ background: "#3B82F6" }} />
            <b>{allActive.length}</b> Total
          </div>
          <div className="bse-pill">
            <span className="bse-pill-dot" style={{ background: "#D97706" }} />
            <b>{managers}</b> {t("managers")}
          </div>
          <div className="bse-pill">
            <span className="bse-pill-dot" style={{ background: "#059669" }} />
            <b>{allActive.length - managers}</b> {t("employees")}
          </div>
        </div>

        <div className="bse-search">
          <Search size={14} />
          <input
            type="text"
            placeholder={t("searchByNamePlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="bse-clear" onClick={() => setSearchQuery("")}>
              <X size={10} />
            </button>
          )}
        </div>
      </header>

      <div className="bse-body">
        {filtered.length > 0 && (
          <p
            style={{
              fontSize: 11,
              color: "#9CA3AF",
              fontWeight: 500,
              marginBottom: 10,
            }}
          >
            {filtered.length} {filtered.length === 1 ? "result" : "results"}
            {searchQuery && ` for "${searchQuery}"`}
          </p>
        )}

        {filtered.length === 0 ? (
          <div className="bse-empty">
            <div className="bse-empty-ico">
              <User size={22} color="#9CA3AF" />
            </div>
            <p className="bse-empty-t">
              {searchQuery ? t("noResultsFound") : t("noEmployeesYet")}
            </p>
            <p className="bse-empty-s">
              {searchQuery
                ? t("nothingMatchesSearch")
                : t("tapButtonToAddFirstMember")}
            </p>
          </div>
        ) : (
          <div className="bse-grid">
            {filtered.map((employee, i) => {
              const sal = getSalaryInfo(employee);
              const isMgr = employee.type === 2;
              return (
                <div
                  key={employee.id}
                  className="bse-card"
                  style={{ animationDelay: `${i * 30}ms` }}
                  onClick={() => handleClick(employee)}
                >
                  <div className="bse-card-accent" />

                  <div className="bse-card-top">
                    <div className={`bse-avatar ${isMgr ? "mgr" : ""}`}>
                      <User size={18} color={isMgr ? "#D97706" : "#3B82F6"} />
                    </div>
                    <div className="bse-card-info">
                      <p className="bse-card-name">{employee.name}</p>
                      <p className="bse-card-id">
                        #{String(employee.id).padStart(4, "0")}
                      </p>
                    </div>
                  </div>

                  <span className={`bse-badge ${isMgr ? "mgr" : "emp"}`}>
                    {isMgr ? <Briefcase size={10} /> : <TrendingUp size={10} />}
                    {isMgr ? t("manager") : t("employee")}
                  </span>

                  <div className="bse-salary">
                    <div>
                      <p className="bse-salary-lbl">{sal.label}</p>
                      <p
                        className="bse-salary-amt"
                        style={{ color: sal.color }}
                      >
                        {fmt(sal.amount)}
                      </p>
                    </div>
                    <div
                      className="bse-salary-icon"
                      style={{ background: sal.bg }}
                    >
                      <Calendar size={13} color={sal.color} />
                    </div>
                  </div>

                  <div className="bse-cta">
                    <span>{t("statistics")}</span>
                    <div className="bse-cta-arr">
                      <ChevronRight size={11} color="#3B82F6" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {refreshing && (
        <div className="bse-overlay">
          <div style={{ textAlign: "center" }}>
            <div className="bse-spinner" />
            <p className="bse-spin-lbl">{t("refreshing")}</p>
          </div>
        </div>
      )}
    </div>
  );
}
